import { useDisclosure, type UseDisclosureReturn } from "@chakra-ui/react";
import { useCallback, useEffect, useRef } from "react";

import { isDescendentOf, isInputKeyboardEvent } from "helpers/browserEvent";
import useOnClickOutsidePopover from "hooks/useOnClickOutsidePopover";

interface Props {
  onClickOutside?: () => void;
  selector?: string | string[];
  closeOnEsc?: boolean;
  defaultOpen?: boolean;
  // If you want to pass in your own external disclosure state you can
  disclosureProps?: UseDisclosureReturn;
}

export default function useControlledPopover<
  T extends HTMLElement = HTMLDivElement,
  U extends HTMLElement = HTMLDivElement,
>({ onClickOutside, selector, closeOnEsc = true, defaultOpen, disclosureProps }: Props = {}) {
  const disclosure = useDisclosure({ defaultOpen });
  const contentRef = useRef<T>(null);
  const triggerRef = useRef<U>(null);

  const resolvedDisclosureProps = disclosureProps ?? disclosure;

  const { open, onClose } = resolvedDisclosureProps;

  const clickOutsideHandler = useCallback(() => {
    if (onClickOutside != null) {
      onClickOutside();
    }
    onClose();
  }, [onClickOutside, onClose]);

  useOnClickOutsidePopover(contentRef, triggerRef, clickOutsideHandler, {
    disabled: !open,
    selector,
  });

  const onKeyDown = useCallback(
    (ev: KeyboardEvent) => {
      if (isInputKeyboardEvent(ev) || ev.key !== "Escape") {
        return;
      }
      const selectors = Array.isArray(selector) ? selector : [selector];
      const isTargetDescendent = selectors.some((s) => isDescendentOf(ev.target as HTMLElement, s));
      if (isTargetDescendent) {
        return;
      }
      ev.preventDefault();
      ev.stopPropagation();
      onClose();
    },
    [onClose, selector],
  );

  useEffect(() => {
    if (!open || !closeOnEsc) {
      return () => {};
    }

    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [onKeyDown, closeOnEsc, open]);

  return {
    ...resolvedDisclosureProps,
    contentRef,
    triggerRef,
  };
}
